// ============================================
// COMMUNITY HUB — TYPE DEFINITIONS
// ============================================

// ─── USER & AUTH ───────────────────────────
export type UserPlan = 'starter' | 'pro' | 'enterprise';

export interface User {
  _id: string;
  name: string;
  email: string;
  avatar?: string;
  plan: UserPlan;
  communitiesOwned: number;
  maxCommunities: number; // 0 for starter, 4 for pro, Infinity for enterprise
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// ─── SOCIAL MEDIA ──────────────────────────
export enum SocialPlatform {
  TIKTOK = 'tiktok',
  FACEBOOK = 'facebook',
  YOUTUBE = 'youtube',
  INSTAGRAM = 'instagram',
  X_TWITTER = 'x_twitter',
  LINKEDIN = 'linkedin',
  DISCORD = 'discord',
  SLACK = 'slack',
}

export const PRO_SOCIAL_PLATFORMS: SocialPlatform[] = [
  SocialPlatform.TIKTOK,
  SocialPlatform.FACEBOOK,
];

export const ENTERPRISE_SOCIAL_PLATFORMS: SocialPlatform[] = [
  ...PRO_SOCIAL_PLATFORMS,
  SocialPlatform.YOUTUBE,
  SocialPlatform.INSTAGRAM,
  SocialPlatform.X_TWITTER,
  SocialPlatform.LINKEDIN,
  SocialPlatform.DISCORD,
  SocialPlatform.SLACK,
];

export interface SocialLink {
  platform: SocialPlatform;
  url: string;
  connected: boolean;
  lastSynced?: string;
}

// ─── COMMUNITY ─────────────────────────────
export const COMMUNITY_CATEGORIES = [
  'technology', 'business', 'marketing', 'design',
  'development', 'finance', 'health', 'education',
  'ecommerce', 'lifestyle', 'fitness', 'music',
  'gaming', 'news', 'other',
] as const;

export type CommunityCategory = (typeof COMMUNITY_CATEGORIES)[number];

export type CommunityVisibility = 'public' | 'private' | 'unlisted';

export interface Community {
  _id: string;
  name: string;
  description: string;
  category: CommunityCategory;
  visibility: CommunityVisibility;
  bannerImage?: string;
  avatarImage?: string;
  owner: string | User;
  members: CommunityMember[];
  memberCount: number;
  postCount: number;
  isMonetized: boolean;
  subscriptionPrice?: number; // in cents
  subscriptionCurrency?: string; // USD, EUR, etc.
  socialLinks: SocialLink[];
  tags: string[];
  rules: CommunityRule[];
  createdAt: string;
  updatedAt: string;
}

export interface CommunityRule {
  id: string;
  title: string;
  description: string;
  order: number;
}

export type MemberRole = 'admin' | 'moderator' | 'member';

export interface CommunityMember {
  _id: string;
  user: User | string;
  role: MemberRole;
  joinedAt: string;
  subscriptionActive?: boolean;
  subscriptionExpires?: string;
  badges: Badge[];
}

export interface Badge {
  id: string;
  name: string;
  icon: string;
  color: string;
}

// ─── POSTS ─────────────────────────────────
export type PostType = 'post' | 'poll' | 'event' | 'article' | 'media';

export interface Post {
  _id: string;
  communityId: string;
  type: PostType;
  content: string;
  mediaUrls: string[];
  fileAttachments: FileAttachment[];
  poll?: Poll;
  event?: CommunityEvent;
  author: User | string;
  likes: string[]; // user IDs
  comments: Comment[];
  commentCount: number;
  views: number;
  pinned: boolean;
  edited: boolean;
  editedAt?: string;
  isSoftDeleted: boolean;
  scheduledFor?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FileAttachment {
  id: string;
  name: string;
  url: string;
  size: number;
  mimeType: string;
}

export interface Poll {
  question: string;
  options: PollOption[];
  totalVotes: number;
  allowMultiple: boolean;
  expiresAt?: string;
}

export interface PollOption {
  id: string;
  text: string;
  votes: string[]; // user IDs
  percentage: number;
}

export interface CommunityEvent {
  title: string;
  description?: string;
  date: string;
  time?: string;
  endTime?: string;
  location?: string;
  virtualLink?: string;
  attendeeLimit?: number;
  attendees: string[]; // user IDs
  isPaid: boolean;
  ticketPrice?: number; // in cents
}

// ─── COMMENTS ──────────────────────────────
export interface Comment {
  _id: string;
  postId: string;
  content: string;
  author: User | string;
  likes: string[];
  parentCommentId?: string; // for nested replies
  replies: Comment[];
  edited: boolean;
  editedAt?: string;
  createdAt: string;
}

// ─── MONETIZATION ──────────────────────────
export const PLATFORM_COMMISSION_RATE = 0.15; // 15%

export interface EarningsSummary {
  totalRevenue: number; // total before commission
  platformCommission: number; // 15%
  ownerEarnings: number; // 85%
  subscriberCount: number;
  period: 'daily' | 'weekly' | 'monthly' | 'yearly';
  transactions: Transaction[];
}

export interface Transaction {
  id: string;
  communityId: string;
  subscriberId: string;
  amount: number; // in cents
  platformFee: number; // 15% in cents
  ownerPayout: number; // 85% in cents
  currency: string;
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  date: string;
}

// ─── NOTIFICATIONS ─────────────────────────
export type NotificationType = 
  | 'new_post' 
  | 'new_comment' 
  | 'comment_reply' 
  | 'post_liked' 
  | 'member_joined'
  | 'subscription_renewed'
  | 'earnings_update'
  | 'role_changed'
  | 'post_pinned'
  | 'community_featured';

export interface Notification {
  _id: string;
  type: NotificationType;
  message: string;
  communityId?: string;
  postId?: string;
  read: boolean;
  createdAt: string;
}

// ─── PERMISSIONS ───────────────────────────
export interface PermissionMap {
  canView: boolean;
  canJoin: boolean;
  canPost: boolean;
  canComment: boolean;
  canLike: boolean;
  canVote: boolean;
  canRSVP: boolean;
  canCreateCommunity: boolean;
  canMonetize: boolean;
  canAccessAnalytics: boolean;
  canExportMembers: boolean;
  canCustomBrand: boolean;
  canAccessAPI: boolean;
  allowedSocialPlatforms: SocialPlatform[];
  maxCommunities: number;
  communitiesRemaining: number;
}

// ─── UI STATE ──────────────────────────────
export type SortOption = 'latest' | 'top' | 'pinned' | 'most_commented' | 'my_posts';

export type FilterOption = 'all' | 'popular' | 'newest' | 'trending';

export interface ToastNotification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

// ─── API RESPONSES ─────────────────────────
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: PaginationMeta;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

// ─── FORM STATES ───────────────────────────
export const DEFAULT_COMMUNITY_FORM = {
  name: '',
  description: '',
  category: 'general' as CommunityCategory,
  visibility: 'public' as CommunityVisibility,
  isMonetized: false,
  subscriptionPrice: 0,
  tags: [] as string[],
};

export const DEFAULT_POST_FORM = {
  type: 'post' as PostType,
  content: '',
  mediaUrls: [] as string[],
  files: [] as File[],
};

export const DEFAULT_POLL_FORM = {
  question: '',
  options: ['', ''],
  allowMultiple: false,
  expiresInDays: 7,
};

export const DEFAULT_EVENT_FORM = {
  title: '',
  description: '',
  date: '',
  time: '',
  location: '',
  virtualLink: '',
  attendeeLimit: 0,
  isPaid: false,
  ticketPrice: 0,
};